import { TableHead } from "@/components/ui/table";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type SortDirection = "asc" | "desc" | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

interface SortableTableHeadProps {
  label: string;
  sortKey: string;
  currentSort: SortConfig;
  onSort: (key: string) => void;
  className?: string;
}

const SortableTableHead = ({ label, sortKey, currentSort, onSort, className }: SortableTableHeadProps) => {
  const isActive = currentSort.key === sortKey;
  const Icon = isActive
    ? currentSort.direction === "asc" ? ArrowUp : ArrowDown
    : ArrowUpDown;

  return (
    <TableHead
      className={cn("cursor-pointer select-none hover:text-foreground transition-colors group", className)}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon size={12} className={cn("shrink-0 transition-opacity", isActive ? "opacity-100" : "opacity-0 group-hover:opacity-50")} />
      </span>
    </TableHead>
  );
};

export default SortableTableHead;

/** Cycle sort: null → asc → desc → null */
export function nextSort(current: SortConfig, key: string): SortConfig {
  if (current.key !== key) return { key, direction: "asc" };
  if (current.direction === "asc") return { key, direction: "desc" };
  if (current.direction === "desc") return { key: "", direction: null };
  return { key, direction: "asc" };
}

/** Generic sort comparator for any array. Handles strings, numbers, nulls, dates. */
export function sortData<T>(data: T[], sort: SortConfig, accessor?: (item: T, key: string) => any): T[] {
  if (!sort.key || !sort.direction) return data;
  const dir = sort.direction === "asc" ? 1 : -1;
  return [...data].sort((a, b) => {
    const av = accessor ? accessor(a, sort.key) : (a as any)[sort.key];
    const bv = accessor ? accessor(b, sort.key) : (b as any)[sort.key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
    return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" }) * dir;
  });
}
